// Regelbasierte Prüfung einer Aufnahme vor dem Absenden.
//
// Die Erfassungs-App zeigt das Ergebnis als Liste konkreter Nachforderungen;
// der Server lehnt das Absenden ab, solange Probleme mit severity "fehlt"
// offen sind. "hinweis" darf der Kunde bewusst übergehen.
// Die KI-Prüfung (Etappe 3) ergänzt diese Liste um Dinge, die man nur auf
// den Fotos sieht (fehlende Wände, Schränke, Räume).

import { ROOM_TYPE_LABEL, ROOM_WALL_LABEL, ROOM_WALL_STEPS, WALL_OF_STEP, type PhotoStep, type RoomPhotoMode, type RoomShape, type RoomType } from "./konstanten.ts";

/** Unter diesem Schärfewert (Laplace-Varianz bei ~800 px) gilt ein Foto als unscharf. */
export const BLUR_MIN = 50;
/** Mittlere Helligkeit 0..255: darunter zu dunkel, darüber überbelichtet. */
export const BRIGHTNESS_MIN = 45;
export const BRIGHTNESS_MAX = 240;
/** So viele Rundum-Fotos braucht ein normaler Raum mindestens. */
export const MIN_AROUND_PHOTOS = 2;

export type CheckAddress = {
  kind: "from" | "to";
  street: string | null;
  zip: string | null;
  city: string | null;
  floor: number | null;
  elevator: boolean | null;
  walkDistanceM: number | null;
  /** Neue Adresse steht noch nicht fest (30.10.2026): PLZ und Ort genügen. */
  pending?: boolean;
};

export type CheckRoom = {
  id: string;
  type: RoomType;
  name: string;
  confirmedComplete: boolean;
  cabinets?: "ja" | "nein" | null;
  /** Raum nach Wänden (15.10.2026); null = ältere Aufnahme mit freien Rundum-Fotos. */
  photoMode?: RoomPhotoMode | null;
  roomShape?: RoomShape | null;
};

export type CheckPhoto = {
  id: string;
  roomId: string;
  step: PhotoStep;
  blurScore: number | null;
  brightness: number | null;
};

export type CheckDraft = {
  contactName: string | null;
  phone: string | null;
  email: string | null;
  dateFixed: boolean | null;
  dateFrom: string | null;
  dateTo: string | null;
  consentPrivacy: boolean;
  addresses: CheckAddress[];
  rooms: CheckRoom[];
  photos: CheckPhoto[];
};

export type Problem = {
  code: string;
  severity: "fehlt" | "hinweis";
  message: string;
  /** Wohin die App springt, um das Problem zu beheben. */
  target: { kind: "kontakt" | "termin" | "adresse" | "raeume" | "raum" | "foto" | "einwilligung" | "lampen"; id?: string };
};

const SMALL_ROOMS: readonly RoomType[] = ["flur", "bad", "abstellraum", "balkon"];

export function checkDraft(d: CheckDraft): Problem[] {
  const p: Problem[] = [];
  const fehlt = (code: string, message: string, target: Problem["target"]) =>
    p.push({ code, severity: "fehlt", message, target });
  const hinweis = (code: string, message: string, target: Problem["target"]) =>
    p.push({ code, severity: "hinweis", message, target });

  if (!d.consentPrivacy) fehlt("einwilligung", "Bitte der Datenverarbeitung zustimmen.", { kind: "einwilligung" });

  if (!d.contactName) fehlt("kontakt_name", "Bitte Ihren Namen angeben.", { kind: "kontakt" });
  if (!d.phone && !d.email)
    fehlt("kontakt_weg", "Bitte Telefonnummer oder E-Mail angeben, damit man Sie erreichen kann.", { kind: "kontakt" });

  if (d.dateFixed === null || !d.dateFrom) {
    fehlt("termin", "Bitte den Wunschtermin angeben.", { kind: "termin" });
  } else if (!d.dateFixed) {
    if (!d.dateTo) fehlt("termin_bis", "Bitte angeben, bis wann der Umzug spätestens sein soll.", { kind: "termin" });
    else if (d.dateTo < d.dateFrom)
      fehlt("termin_reihenfolge", "Das Ende des Zeitraums liegt vor dem Anfang.", { kind: "termin" });
  }

  for (const kind of ["from", "to"] as const) {
    const label = kind === "from" ? "Auszugsadresse" : "Einzugsadresse";
    const a = d.addresses.find((x) => x.kind === kind);
    if (kind === "to" && a?.pending) {
      if (!a.zip || !a.city) fehlt("adresse_to", `${label}: PLZ und Ort angeben (die genaue Adresse darf noch offen sein).`, { kind: "adresse", id: kind });
      continue;
    }
    if (!a || !a.street || !a.zip || !a.city) {
      fehlt(`adresse_${kind}`, `${label}: Straße, PLZ und Ort angeben.`, { kind: "adresse", id: kind });
      continue;
    }
    if (a.floor === null) fehlt(`etage_${kind}`, `${label}: Etage angeben.`, { kind: "adresse", id: kind });
    if (a.elevator === null) fehlt(`aufzug_${kind}`, `${label}: Gibt es einen Aufzug?`, { kind: "adresse", id: kind });
    if (a.walkDistanceM === null)
      fehlt(`laufweg_${kind}`, `${label}: Wie weit ist es zum nächsten Parkplatz?`, { kind: "adresse", id: kind });
  }

  if (d.rooms.length === 0) {
    fehlt("keine_raeume", "Bitte mindestens einen Raum aufnehmen.", { kind: "raeume" });
    return p;
  }

  // Seit 08.10.2026 nur noch echte Lücken: Türfoto (wird in der Aufnahme schon
  // einmal erfragt), fehlender Flur oder fehlende Küche (eigene Raumauswahl)
  // sind keine Hinweise mehr – sie widersprachen den Antworten der Kundin.

  for (const room of d.rooms) {
    const photos = d.photos.filter((f) => f.roomId === room.id);
    const name = room.name || ROOM_TYPE_LABEL[room.type];
    if (photos.length === 0) {
      fehlt("raum_ohne_fotos", `${name}: noch keine Fotos.`, { kind: "raum", id: room.id });
      continue;
    }
    // Nach Wänden (15.10.2026): jede fehlende Wand einzeln nennen; die Kurzform braucht beide Fotos.
    if (room.photoMode === "waende") {
      const missing = ROOM_WALL_STEPS.filter((st) => !photos.some((f) => f.step === st)).map((st) => ROOM_WALL_LABEL[WALL_OF_STEP[st]!]);
      if ((room.roomShape === "l_foermig" || room.roomShape === "nische") && !photos.some((f) => f.step === "wand_nische")) missing.push(ROOM_WALL_LABEL.nische);
      if (missing.length > 0)
        hinweis("raum_wand_fehlt", `${name}: Es fehlt noch ein Foto: ${missing.join(", ")}.`, { kind: "raum", id: room.id });
    } else if (room.photoMode === "kurz") {
      if (!photos.some((f) => f.step === "klein_rein") || !photos.some((f) => f.step === "klein_zurueck"))
        hinweis("raum_wand_fehlt", `${name}: Bitte ein Foto aus der Tür hinein und eines aus der hintersten Ecke zurück zur Tür.`, { kind: "raum", id: room.id });
    }
    const around = photos.filter((f) => f.step === "rundum").length;
    if (!room.photoMode && !SMALL_ROOMS.includes(room.type) && around < MIN_AROUND_PHOTOS)
      hinweis(
        "raum_wenig_fotos",
        `${name}: erst ${around} Foto. Ein zweites aus der anderen Ecke hilft, alle Möbel zu sehen.`,
        { kind: "raum", id: room.id },
      );
    if (!room.confirmedComplete)
      hinweis("raum_nicht_bestaetigt", `${name}: Bitte bestätigen, dass der Raum vollständig ist.`, {
        kind: "raum",
        id: room.id,
      });
    // Nur wenn die Kundin selbst "Ja, große Schränke" gesagt hat und kein Foto folgte.
    if (room.cabinets === "ja" && !photos.some((f) => f.step === "schrank_offen"))
      hinweis("schrank_zu", `${name}: Sie haben große Schränke angegeben – ein Foto mit offenen Türen fehlt noch.`, {
        kind: "raum",
        id: room.id,
      });
    // Lampenfotos (glatte Decke, leuchtende Lampe) nicht nach Schärfe und Helligkeit beurteilen.
    for (const f of photos.filter((x) => x.step !== "lampe")) {
      // Helligkeit vor Schärfe: dunkle Fotos haben auch wenig Kontrast.
      if (f.brightness !== null && f.brightness < BRIGHTNESS_MIN)
        hinweis("foto_dunkel", `${name}: Ein Foto ist zu dunkel. Bitte Licht einschalten und neu aufnehmen.`, {
          kind: "foto",
          id: f.id,
        });
      else if (f.brightness !== null && f.brightness > BRIGHTNESS_MAX)
        hinweis("foto_hell", `${name}: Ein Foto ist überbelichtet. Bitte nicht gegen das Fenster fotografieren.`, {
          kind: "foto",
          id: f.id,
        });
      else if (f.blurScore !== null && f.blurScore < BLUR_MIN)
        hinweis("foto_unscharf", `${name}: Ein Foto ist unscharf. Bitte neu aufnehmen.`, { kind: "foto", id: f.id });
    }
  }

  return p;
}

export function blockingProblems(problems: Problem[]): Problem[] {
  return problems.filter((x) => x.severity === "fehlt");
}
